// The gap cue for a drilled sentence (design: docs/design/phrase-drill-design.md §5).
//
// A phrase Term carries a focus form — which token(s) of it realize its headword, derived at add
// time by the Java FocusWordMatcher and stored on term.focus_form. This module turns a phrase plus
// its focus form into a render plan: the sentence with those tokens blanked, so the learner types
// one word instead of forty characters. "Type only the actual word" NECESSARILY means showing the
// German frame — with the English alone, "The rumour turned out to be false." → type one word is
// unanswerable, because nothing says which word is wanted.
//
// Pure string -> plan, no DOM access; training_session.js owns the echo and the answer check, the
// same way answer_forms.js serves the reverse direction.
var PhraseGap = (function () {
	"use strict";

	// Word tokens — the same shape FocusWordMatcher.TOKEN (Java) matched on: letters and digits,
	// keeping internal hyphens and apostrophes ("E-Mail", "geht's"). The stored focus form is a
	// surface form lifted straight out of this phrase, so the two tokenizers MUST agree or a stored
	// form would not be findable in the text it came from.
	var TOKEN = /[\p{L}\p{N}]+(?:[-'’][\p{L}\p{N}]+)*/gu;

	// A phrase reads as a SENTENCE when it closes like one — final punctuation, optionally inside a
	// closing quote or bracket (the corpus has dialogue examples that end 「…!" 」).
	//
	// This is a text test, not a lookup: a phrase Term does not record whether it came from
	// sense_collocation or sense_example, and "is this a sentence" is the question the design
	// actually asks. A collocation that IS a full clause ("Das stimmt.") is a sentence for this
	// purpose, and a sentence stripped of its full stop is not — both are the right answer.
	var SENTENCE_END = /[.!?…]["'”“„»«’)\]]*\s*$/;

	function isSentence(text) {
		return !!text && SENTENCE_END.test(String(text));
	}

	// "hole,ab" -> ["hole", "ab"]. A multi-part form is a separable verb split across the clause.
	function parts(focusForm) {
		var out = [];
		var raw = String(focusForm == null ? "" : focusForm).split(",");
		for (var i = 0; i < raw.length; i++) {
			var p = raw[i].trim();
			if (p) { out.push(p); }
		}
		return out;
	}

	// The render plan, or null when the phrase must be typed in full.
	//
	// {segments: [{text} | {gap: i, form}], gaps: [surface form per gap], text: the phrase}
	//
	// Matching is case-SENSITIVE, because the stored form is the surface form as it appears here.
	// That is what keeps "Ab morgen hole ich dich ab." asking for two words and not three: the
	// sentence-initial "Ab" is a different word from the particle "ab", and German capitalization
	// says so. A genuine repeat — the same form, same spelling, twice — still gaps every occurrence,
	// which is why the focus form is stored as text rather than as an offset.
	function plan(phraseText, focusForm) {
		var ps = parts(focusForm);
		if (!phraseText || !ps.length) { return null; }
		var text = String(phraseText);
		var wanted = {};
		for (var i = 0; i < ps.length; i++) { wanted[ps[i]] = true; }

		var segments = [], gaps = [], last = 0, total = 0, m;
		TOKEN.lastIndex = 0;
		while ((m = TOKEN.exec(text)) !== null) {
			total++;
			if (!wanted.hasOwnProperty(m[0])) { continue; }
			if (m.index > last) { segments.push({text: text.substring(last, m.index)}); }
			segments.push({gap: gaps.length, form: m[0]});
			gaps.push(m[0]);
			last = m.index + m[0].length;
		}
		if (!gaps.length) { return null; }
		// Every token gapped = the headword IS the phrase, which happens when a typed-phrase entry's
		// example repeats its own text ("Die Kinder haben sich heute gut benommen." stored under a
		// term of the same text). Faithful, but blanking the whole line asks nothing — so this is
		// treated exactly like an unresolved focus form: type it in full.
		if (gaps.length >= total) { return null; }
		if (last < text.length) { segments.push({text: text.substring(last)}); }
		return {segments: segments, gaps: gaps, text: text};
	}

	// What the learner has to produce: the gapped forms in READING order, which is the order they
	// meet them on screen. (The stored order is candidate order — the same thing in every case the
	// corpus actually produces, but the two are not the same statement.)
	function expected(p) {
		return p ? p.gaps.join(" ") : "";
	}

	// One input field feeds N gaps: whitespace-separated words fill them left to right and the last
	// gap takes whatever remains, so a two-part answer ("hole ab") lands one part per gap and a
	// still-being-typed "hol" sits in the first.
	function distribute(typed, count) {
		var words = String(typed == null ? "" : typed).trim().split(/\s+/);
		var used = [];
		for (var i = 0; i < words.length; i++) { if (words[i]) { used.push(words[i]); } }
		if (count <= 1) { return [used.join(" ")]; }
		var out = [];
		for (var g = 0; g < count - 1; g++) { out.push(used[g] || ""); }
		out.push(used.slice(count - 1).join(" "));
		return out;
	}

	// The frame as plain text, blanks and all — what the answer history records as the cue actually
	// shown. Never the un-gapped sentence: that would write the answer into the cue column.
	function plainText(p) {
		var out = "";
		for (var i = 0; i < p.segments.length; i++) {
			out += p.segments[i].gap == null ? p.segments[i].text : "___";
		}
		return out;
	}

	function escapeHtml(s) {
		return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
	}

	// The frame as HTML: literal runs plus one span per gap. The spans start empty and are filled
	// live by training_session.js as the user types; `revealed` renders the correct forms in place
	// instead, which is what the answer reveal shows.
	//
	// `revealed` marks the FRAME as well as each gap, because the two states want different type:
	// while the sentence is a cue it is context under the question and stays quiet, and once it
	// carries the answer it becomes the thing being taught. Only the drill styles that today
	// (exercise.css); the Learn ladder shares this module and simply ignores the extra class.
	function frameHtml(p, revealed) {
		var html = "<div class='_gapFrameClass" + (revealed ? " _gapFrameRevealedClass" : "") + "'>";
		for (var i = 0; i < p.segments.length; i++) {
			var seg = p.segments[i];
			if (seg.gap == null) {
				html += escapeHtml(seg.text);
			} else {
				html += "<span class='_gapClass" + (revealed ? " _gapRevealedClass" : "")
					+ "' data-gap='" + seg.gap + "'>"
					+ (revealed ? escapeHtml(seg.form) : "") + "</span>";
			}
		}
		return html + "</div>";
	}

	return {
		isSentence: isSentence,
		parts: parts,
		plan: plan,
		expected: expected,
		distribute: distribute,
		plainText: plainText,
		frameHtml: frameHtml
	};
})();
